import { Row, Table } from '@tanstack/react-table'
import ExcelJS, { TableStyleProperties } from 'exceljs'

import { useFramework } from '@suntex.fim/framework'

const getGrade = (value: number) => {
  if (value === 50) {
    return 'Other'
  }
  if (value > 0) {
    return value.toLocaleString()
  }
  if (value === 0) {
    return 0
  }
  return '-'
}

function useExport<T>({
  table,
  hidableSelectLabel = {},
  fileName = 'report',
  hasColumnChange = [],
  transformCols = () => {},
  isSchool = false
}: {
  table: Table<T>
  hidableSelectLabel: Record<string, string>
  fileName: string
  hasColumnChange?: string[]
  transformCols?: any
  isSchool?: boolean
}) {
  const { localization } = useFramework()
  interface RowData {
    numberPretest: number
    numberFluentPretest: number
    numberFluent: number
    numberPosttest: number
    numberProficientPretest: number
    numberProficient: number
  }
  const exportSelectedToExcel = async () => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('sheet1')
    let columns: any[]
    if (isSchool) {
      columns = table
        .getAllColumns()
        .filter(({ id }) => Object.keys(hidableSelectLabel).includes(id))
    } else {
      columns = table
        .getVisibleFlatColumns()
        .filter(({ id }) => Object.keys(hidableSelectLabel).includes(id))
    }
    const row = table.getPrePaginationRowModel().flatRows
    const selectedData = row.map((row) =>
      columns.reduce((acc, { id }) => {
        const original = (row as unknown as Row<RowData>).original
        if (hasColumnChange.includes(id)) {
          if (id === 'numberFluentPretest') {
            const calculatedValue =
              original.numberFluentPretest + original.numberProficientPretest
            acc[hidableSelectLabel[id]] =
              calculatedValue >= 0 ? `${calculatedValue}` : '-'
          } else if (id === 'preProficient') {
            const calculatedValue =
              original.numberFluentPretest + original.numberProficientPretest
            const percent = (calculatedValue / original.numberPretest) * 100
            acc[hidableSelectLabel[id]] =
              percent >= 0 ? `${percent.toFixed(1)}%` : '-'
          } else if (id === 'numberFluent') {
            const calculatedValue =
              original.numberFluent + original.numberProficient
            acc[hidableSelectLabel[id]] =
              calculatedValue >= 0 ? `${calculatedValue}` : '-'
          } else if (id === 'postProficient') {
            const calculatedValue =
              original.numberFluent + original.numberProficient
            const percent = (calculatedValue / original.numberPosttest) * 100
            acc[hidableSelectLabel[id]] =
              percent >= 0 ? `${percent.toFixed(1)}%` : '-'
          } else if (id === 'teamGrade' || id === 'grade') {
            const value = getGrade(row?.renderValue(id))
            acc[hidableSelectLabel[id]] = value
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const newCols = transformCols(id, row.renderValue(id))
          return { ...acc, ...newCols }
        }
        acc[hidableSelectLabel[id]] = row.renderValue(id)?.toLocaleString()
        return acc
      }, {})
    )
    const headers = Object.keys(selectedData[0]).map((col) => ({
      name: localization.text(col),
      key: col,
      filterButton: true
    }))
    // Add headers
    worksheet.addTable({
      name: 'test',
      ref: 'A1',
      headerRow: true,
      totalsRow: false,
      style: {
        theme: null as unknown as TableStyleProperties['theme'],
        showRowStripes: false
      },
      columns: headers || [{ name: '' }],
      rows: selectedData.map((e) => {
        const newRow: any[] = []
        headers.forEach((column) => {
          newRow.push(e[column.key])
        })
        return newRow
      })
    })

    // Make header row bold
    worksheet.getRow(1).font = { bold: true }

    // Set column width dynamically based on content
    headers.forEach((header, index) => {
      const column = worksheet.getColumn(index + 1)
      const headerWidth = header.name.toString().length
      const width = headerWidth + 3
      column.width = width + 2 // Add padding
    })

    // Save workbook to a buffer
    const buffer = await workbook.xlsx.writeBuffer()

    // Create a blob from buffer
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })

    // Trigger file download
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${fileName}.xlsx`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  return {
    exportSelectedToExcel
  }
}

export default useExport
